import {AnimatePresence, m} from 'framer-motion';
import {Fragment} from 'react';
import {opacityAnimation} from '@common/ui/animation/opacity-animation';
import {Skeleton} from '@common/ui/skeleton/skeleton';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import {useAuth} from '@common/auth/use-auth';
import clsx from 'clsx';
import {Chip} from '@common/ui/forms/input-field/chip-field/chip';
import {Trans} from '@common/i18n/trans';
import {FormattedPrice} from '@common/i18n/formatted-price';
import {Button} from '@common/ui/buttons/button';
import {Link} from 'react-router-dom';
import {setInLocalStorage} from '@common/utils/hooks/local-storage';
import {ProductFeatureList} from '@common/billing/pricing-table/product-feature-list';

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
}

export function PricingTable({
  selectedCycle,
  className,
  productLoader,
}: PricingTableProps) {
  const query = useProducts(productLoader);

  return (
    <div className={clsx('w-full', className)}>
      <AnimatePresence initial={false} mode="wait">
        {query.data ? (
          <PlanList
            key="plan-list"
            plans={query.data.products}
            selectedPeriod={selectedCycle}
          />
        ) : (
          <SkeletonLoader key="skeleton-loader" />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
}

function PlanList({plans, selectedPeriod}: PlanListProps) {
  const {isLoggedIn, isSubscribed} = useAuth();
  const filteredPlans = plans.filter(plan => !plan.hidden);

  return (
    <div className="w-full">
      <div className="flex w-full justify-center">
        <div
          className={clsx(
            'w-full max-w-[1600px]',
            'grid grid-cols-1 gap-24',
            'md:grid-cols-2 md:gap-28',
            'xl:grid-cols-4 xl:gap-24',
          )}
        >
          {filteredPlans.map(plan => {
            const price = findBestPrice(selectedPeriod, plan.prices);

            let upgradeRoute: string | undefined;
            if (!isLoggedIn) {
              upgradeRoute = `/register?redirectFrom=pricing`;
            }
            if (isSubscribed) {
              upgradeRoute = `/change-plan/${plan.id}/${price?.id}/confirm`;
            }
            if (isLoggedIn && !plan.free) {
              upgradeRoute = `/checkout/${plan.id}/${price?.id}`;
            }

            // Border highlight only for recommended plan
            const cardBorder = plan.recommended
              ? 'border-2 border-primary'
              : 'border border-divider';

            return (
              <m.div
                key={plan.id}
                {...opacityAnimation}
                className={clsx(
                  'relative rounded-panel bg shadow-lg',
                  cardBorder,
                  'px-22 py-26 md:px-26 md:py-30 lg:px-28',
                  'flex w-full flex-col justify-self-center',
                  'md:w-[360px]',
                  'lg:w-[380px]',
                  'xl:w-[320px]',
                )}
              >
                {/* Most popular badge (top-right corner) */}
                {plan.recommended && (
                  <div className="absolute -top-12 right-16 z-10">
                    <Chip
                      radius="rounded"
                      size="sm"
                      className="bg-primary px-12 py-4 text-xs font-medium text-white shadow-sm"
                    >
                      <Trans message="Most popular" />
                    </Chip>
                  </div>
                )}

                <div className="mb-26 text-center">
                  <div className="mb-16 text-3xl font-semibold tracking-tight md:text-[34px]">
                    <Trans message={plan.name} />
                  </div>

                  <div className="mx-auto max-w-[40ch] text-sm text-muted md:text-[15px]">
                    <Trans message={plan.description} />
                  </div>
                </div>

                <div className="mt-4 flex justify-center">
                  <div className="text-center">
                    {price ? (
                      <FormattedPrice
                        priceClassName="font-bold text-4xl leading-tight md:text-5xl"
                        periodClassName="text-muted text-xs md:text-sm"
                        variant="separateLine"
                        price={price}
                      />
                    ) : (
                      <div className="text-4xl font-bold leading-tight md:text-5xl">
                        <Trans message="Free" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-22">
                  <Button
                    variant={plan.recommended ? 'flat' : 'outline'}
                    color="primary"
                    className="w-full"
                    size="md"
                    elementType={upgradeRoute ? Link : undefined}
                    disabled={!upgradeRoute}
                    onClick={() => {
                      if (isLoggedIn || !price || !plan) return;
                      setInLocalStorage('be.onboarding.selected', {
                        productId: plan.id,
                        priceId: price.id,
                      });
                    }}
                    to={upgradeRoute}
                  >
                    {plan.free ? (
                      <Trans message="Get started" />
                    ) : (
                      <Trans message="Upgrade" />
                    )}
                  </Button>
                </div>

                <div className="mt-20 flex-1">
                  <ProductFeatureList product={plan} />
                </div>
              </m.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="grid grid-cols-1 gap-24 md:grid-cols-2 md:gap-28 xl:grid-cols-4 xl:gap-30">
      <PlanSkeleton key="skeleton-1" />
      <PlanSkeleton key="skeleton-2" />
      <PlanSkeleton key="skeleton-3" />
      <PlanSkeleton key="skeleton-4" />
    </div>
  );
}

function PlanSkeleton() {
  return (
    <m.div
      {...opacityAnimation}
      className="w-full rounded-panel border bg px-22 py-26 shadow-lg md:px-26 md:py-30 lg:px-30"
    >
      <Skeleton className="my-10" />
      <Skeleton className="mb-40" />
      <Skeleton className="mb-40 h-30" />
      <Skeleton className="mb-40 h-40" />
      <Skeleton className="mb-20" />
      <Skeleton />
      <Skeleton />
    </m.div>
  );
}
